import { useParams, Link, useLocation } from 'react-router-dom'
import { ArrowLeft, Target, ListChecks, BarChart3, FileText } from 'lucide-react'
import { useEffect, useState } from 'react'
import BSDCard from '../BSDCard'

interface SchedaPpt {
  id: number
  area: string
  titolo: string
  descrizione: string
  indicatori: string
  azioni: string
  percentuale: string
}

const AREA_COLORS: Record<string, string> = {
  'Rete Territoriale e Offerta Sociosanitaria': '#6366f1',
  'Partecipazione della Comunità e del Terzo Settore': '#f59e0b',
  'Integrazione Sociosanitaria-Sociale': '#10b981',
  'Valutazione Multidimensionale': '#ef4444',
  'Continuità dell\'Assistenza': '#8b5cf6',
  'Lo Sviluppo delle Cure Domiciliari': '#ec4899',
  'La Presa in Carico dei Cronici e dei Fragili': '#14b8a6',
  'Sviluppo Telemedicina': '#f97316',
  'Area Materno Infantile': '#06b6d4',
  'Area Salute Mentale': '#84cc16',
  'Psicologia delle Cure Primarie e di Comunità e delle Cure Primarie': '#e11d48',
  'Prevenzione e Promozione della Salute': '#7c3aed',
  'Progetti Trasversali': '#0ea5e9',
}

export function pptSchedaSlug(titolo: string): string {
  return titolo
    .toLowerCase()
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

export default function PptDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const location = useLocation()
  const state = location.state as { from?: string } | null
  const backTo = state?.from ?? '/poloterritoriale'

  const [schede, setSchede] = useState<SchedaPpt[]>([])
  useEffect(() => {
    fetch('/data/ppt.json')
      .then(r => r.json())
      .then((d: SchedaPpt[]) => setSchede(d))
  }, [])

  const scheda = schede.find(s => pptSchedaSlug(s.titolo) === slug)

  if (schede.length > 0 && !scheda) {
    return (
      <div className="cdc-detail-page">
        <div className="cdc-detail-container">
          <Link to={backTo} className="cdc-detail-back"><ArrowLeft size={16} /> Torna</Link>
          <h1>Obiettivo non trovato</h1>
        </div>
      </div>
    )
  }

  if (!scheda) {
    return (
      <div className="cdc-detail-page">
        <div className="cdc-detail-container">
          <p style={{ color: 'var(--text-3)', padding: 40, textAlign: 'center' }}>Caricamento…</p>
        </div>
      </div>
    )
  }

  const color = AREA_COLORS[scheda.area] ?? '#6366f1'
  const perc = parseFloat(scheda.percentuale)

  return (
    <div className="cdc-detail-page">
      <div className="cdc-detail-container">
        <Link to={backTo} className="cdc-detail-back"><ArrowLeft size={16} /> Torna</Link>

        {/* Header */}
        <div className="cdc-detail-header">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999,
              color, background: `${color}18`, border: `1px solid ${color}44`,
            }}>
              {scheda.area}
            </span>
          </div>
          <h1 className="cdc-detail-title">{scheda.titolo}</h1>
        </div>

        {/* Progress */}
        {!isNaN(perc) && (
          <BSDCard kind="custom" className="cdc-detail-info-card" sezione="Polo Territoriale">
            <h2 className="cdc-detail-section-title"><BarChart3 size={16} /> Stato di avanzamento</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12 }}>
              <div style={{ flex: 1, height: 10, borderRadius: 5, background: 'var(--border)', overflow: 'hidden' }}>
                <div style={{ width: `${perc}%`, height: '100%', borderRadius: 5, background: color, transition: 'width 0.3s' }} />
              </div>
              <span style={{ fontSize: 18, fontWeight: 800, color, flexShrink: 0 }}>{perc}%</span>
            </div>
          </BSDCard>
        )}

        {/* Content grid */}
        <div className="cdc-detail-row">
          {scheda.descrizione && (
            <BSDCard kind="custom" className="cdc-detail-info-card" sezione="Polo Territoriale">
              <h2 className="cdc-detail-section-title"><FileText size={16} /> Descrizione</h2>
              <p style={{ color: 'var(--text-1)', fontSize: 13, lineHeight: 1.7, margin: '12px 0 0', whiteSpace: 'pre-line' }}>
                {scheda.descrizione}
              </p>
            </BSDCard>
          )}

          {scheda.indicatori && (
            <BSDCard kind="custom" className="cdc-detail-info-card" sezione="Polo Territoriale">
              <h2 className="cdc-detail-section-title"><Target size={16} /> Indicatori</h2>
              <p style={{ color: 'var(--text-1)', fontSize: 13, lineHeight: 1.7, margin: '12px 0 0', whiteSpace: 'pre-line' }}>
                {scheda.indicatori}
              </p>
            </BSDCard>
          )}

          {scheda.azioni && (
            <BSDCard kind="custom" className="cdc-detail-info-card" sezione="Polo Territoriale">
              <h2 className="cdc-detail-section-title"><ListChecks size={16} /> Azioni</h2>
              <p style={{ color: 'var(--text-1)', fontSize: 13, lineHeight: 1.7, margin: '12px 0 0', whiteSpace: 'pre-line' }}>
                {scheda.azioni}
              </p>
            </BSDCard>
          )}
        </div>
      </div>
    </div>
  )
}
